import { createNode, DataValue } from '@glueapp/graph-types';

export const Reduce = createNode({
    id: 'Reduce',
    name: 'Reduce',
    description:
        'The general left fold: collapse a list into a single accumulated value using a combiner function. The accumulator can be a different type than the list items (e.g. reduce a list of numbers into a text). If the accumulator and item types are the same and you do not need a seed, use "Combine" instead.',
    aiDescription: [
        'General left fold over a list (like Array.reduce). Collapses a list into one accumulated value with a combiner function. Async — uses executeFunctionNode; steps run sequentially because each depends on the previous accumulator.',
        'Use this when the accumulator type differs from the element type (e.g. numbers -> text) or you need an explicit seed. When the accumulator and element types are the SAME and no seed is needed, prefer the friendlier "Combine" node.',
        'Setup steps:',
        '1. Create a function with inputs "Accumulator" (type A), "Item" (type T), "Index" (number), "List" (array of T) and output "Result" (type A).',
        '   Inside the function, produce the next accumulator value as "Result".',
        '2. Add this node: glue add "Reduce".',
        '3. Connect a list to "List".',
        '4. Bind the function to the "Combiner" port (kind: function) by SETTING its value to a function reference (not a connection): set_value <thisNodeId> "Combiner" {"isFunctionReferenceValue":true,"functionId":"<functionId>"}.',
        '5. Connect an "Initial Value" (type A) as the seed. If left unconnected, the generated default value for type A is used.',
        'Semantics: acc starts at "Initial Value", then acc = Combiner(acc, item, index, list) left-to-right. An empty list returns the seed. Never throws.',
        'Output: "Result" (type A), the final accumulated value.',
    ].join('\n'),
    category: 'List',
    color: '#778ed5',
    isAsync: true, // Function execution is async

    inPorts: {
        List: {
            kind: 'array',
            subType: {
                kind: 'generic',
                identifier: 'T',
            },
        },
        Combiner: {
            kind: 'function',
            inputTypes: {
                Accumulator: { kind: 'generic', identifier: 'A' },
                Item: { kind: 'generic', identifier: 'T' },
                Index: { kind: 'number' },
                List: {
                    kind: 'array',
                    subType: { kind: 'generic', identifier: 'T' },
                },
            },
            outputTypes: {
                Result: { kind: 'generic', identifier: 'A' },
            },
        },
        'Initial Value': { kind: 'generic', identifier: 'A' },
    },

    outPorts: {
        Result: { kind: 'generic', identifier: 'A' },
    },

    execute: async ({ inPorts, executeFunctionNode }) => {
        if (!executeFunctionNode) {
            throw new Error('Function execution not available');
        }

        const list = inPorts['List'] as DataValue[];
        const combinerValue = inPorts['Combiner'];

        if (
            !combinerValue ||
            typeof combinerValue !== 'object' ||
            !('isFunctionReferenceValue' in combinerValue) ||
            !combinerValue.isFunctionReferenceValue
        ) {
            throw new Error('Combiner must be a function reference');
        }

        const combiner = combinerValue as { functionId: string };

        // Seed: the Initial Value. The port is required, so graph type resolution
        // already fills an unconnected port with the default value for type A.
        let accumulator: DataValue = inPorts['Initial Value'] ?? null;

        if (!Array.isArray(list)) {
            return { output: { Result: accumulator } };
        }

        // Sequential fold — each step depends on the previous accumulator.
        for (let index = 0; index < list.length; index++) {
            const result = await executeFunctionNode(combiner.functionId, {
                Accumulator: accumulator,
                Item: list[index],
                Index: index,
                List: list,
            });
            accumulator = result.output['Result'];
        }

        return { output: { Result: accumulator } };
    },
});
